import type { HoveredQuote } from '../hooks/useQuoteComments';
import { dateTime } from '../lib/format';

/** Its `max-inline-size`, needed here to keep it inside the viewport. */
const TOOLTIP_WIDTH = 320;
/** Enough to clear the pointer without landing on the next line of text. */
const POINTER_GAP = 14;
/**
 * Below which the tooltip goes above the pointer instead. An estimate — the
 * height depends on how much was written — and being wrong about it costs a
 * tooltip drawn slightly high, not one off screen.
 */
const ASSUMED_HEIGHT = 120;

interface QuoteCommentTooltipProps {
  hovered: HoveredQuote;
}

/**
 * What the comments on a passage say, on hover over the passage itself.
 *
 * The highlight in the reader says a passage was commented on; until now, what
 * was said lived only in the Notes tab, which meant reading a marked sentence
 * cost a glance across the screen and a scan of the thread for the matching
 * quote. This closes that loop in the other direction from `revealQuote`.
 *
 * It is a read, never a target: `pointer-events: none`, so it cannot swallow a
 * click on the text underneath or flicker as the pointer reaches for it. Acting
 * on a comment still happens in the tab.
 */
export function QuoteCommentTooltip({ hovered }: QuoteCommentTooltipProps) {
  // Physical `left`/`top`, as in the right-click menu: the coordinates come from
  // the pointer, which does not mirror under `dir="rtl"`.
  const isFlippedUp =
    hovered.y + POINTER_GAP + ASSUMED_HEIGHT > window.innerHeight;
  const left = Math.min(
    Math.max(hovered.x - TOOLTIP_WIDTH / 2, POINTER_GAP),
    window.innerWidth - TOOLTIP_WIDTH - POINTER_GAP
  );

  return (
    <div
      className="gr-qtip"
      role="tooltip"
      style={
        isFlippedUp
          ? { left, bottom: window.innerHeight - hovered.y + POINTER_GAP }
          : { left, top: hovered.y + POINTER_GAP }
      }
    >
      {hovered.comments.map((comment) => (
        <div className="gr-qtip-item" key={comment._id}>
          <div className="gr-qtip-top">
            <span className="gr-qtip-who">
              {comment.authorName ?? comment.employeeId}
            </span>
            <span className="gr-qtip-when">{dateTime(comment.createdAt)}</span>
          </div>
          <p className="gr-qtip-body">{comment.text}</p>
        </div>
      ))}
    </div>
  );
}
